import React, { useEffect, useMemo, useRef, useState } from "react";

import { icons } from "~/ui";
import { tw } from "~/utils";
import {
  applyPad,
  combineDate,
  DATE_SEGMENTS,
  DateSegments,
  formatPhoneNumber,
  removeNonDigits,
} from "../utils";

type InputType =
  "text" | "email" | "tel" | "date" | "select" | "textarea" | "dob";

type FormInputProps = {
  label: string;
  name: string;
  type: InputType;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  options?: Array<{ value: string; label: string }>;
  className?: string;
  showRequiredIndicator?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
};

type SelectInputProps = {
  inputId: string;
  validationAttributes?: ValidationAttributes;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  error?: string;
  className?: string;
  disabled?: boolean;
};

type TelInputProps = {
  inputId: string;
  validationAttributes?: ValidationAttributes;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
};

type DateInputProps = {
  inputId: string;
  validationAttributes?: ValidationAttributes;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  className?: string;
};

type DateOfBirthInputProps = {
  inputId: string;
  labelId?: string;
  required?: boolean;
  validationAttributes?: ValidationAttributes;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  className?: string;
};

type DefaultInputProps = {
  inputId: string;
  validationAttributes?: ValidationAttributes;
  name: string;
  type: InputType;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  error?: string;
  className?: string;
};

const usPhoneNumberPrefix = "+1";

const labelClassName = "block font-sans text-sm text-gray-900";

type ValidationAttributes = {
  "aria-required": boolean;
  "aria-invalid": boolean;
  "aria-describedby"?: string;
};

const SelectInput = ({
  inputId,
  name,
  value,
  onChange,
  onBlur,
  options,
  placeholder,
  error,
  validationAttributes,
  className,
  disabled,
}: SelectInputProps) => (
  <select
    id={inputId}
    name={name}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onBlur={onBlur}
    disabled={disabled}
    {...validationAttributes}
    className={tw(
      "focus:border-indigo focus:ring-indigo border-1 w-full rounded-lg border-neutral-400 p-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1",
      error && "border-red-500 focus:border-red-500 focus:ring-red-500",
      disabled && "cursor-not-allowed bg-neutral-100 text-neutral-500",
      className,
    )}
  >
    <option value="">{placeholder || "Please select"}</option>
    {options.map((option: { value: string; label: string }) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

const TelInput = ({
  inputId,
  name,
  value,
  onChange,
  onBlur,
  error,
  validationAttributes,
}: TelInputProps) => (
  <div
    className={tw(
      "flex overflow-hidden rounded-lg border border-neutral-400",
      error && "border-red-500 focus:border-red-500 focus:ring-red-500",
    )}
  >
    <div className="bg-light-lavender-gray flex items-center justify-center px-5 py-2 text-sm text-gray-600">
      {usPhoneNumberPrefix}
    </div>
    <input
      id={inputId}
      name={name}
      type="tel"
      value={value}
      placeholder="(000) 000-0000"
      onChange={(e) => {
        const formattedValue = formatPhoneNumber(e.target.value);
        onChange(formattedValue);
      }}
      onBlur={onBlur}
      {...validationAttributes}
      className={tw(
        "focus:border-indigo focus:ring-indigo w-full p-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1",
        error && "border-red-500 focus:border-red-500 focus:ring-red-500",
      )}
    />
  </div>
);

const DateInput = ({
  inputId,
  name,
  value,
  onChange,
  onBlur,
  error,
  validationAttributes,
  className,
}: DateInputProps) => {
  const formatValueForInput = (dateString: string) => {
    if (!dateString) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const [month, day, year] = dateString.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    return "";
  };

  const formatValueForForm = (dateString: string) => {
    if (!dateString) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-");
      return `${month}/${day}/${year}`;
    }

    return dateString;
  };

  return (
    <input
      id={inputId}
      name={name}
      type="date"
      value={formatValueForInput(value)}
      onChange={(e) => onChange(formatValueForForm(e.target.value))}
      onBlur={onBlur}
      {...validationAttributes}
      className={tw(
        "focus:border-indigo focus:ring-indigo border-1 w-full rounded-lg border-neutral-400 p-2.5 text-gray-900 focus:outline-none focus:ring-1",
        error && "border-red-500 focus:border-red-500 focus:ring-red-500",
        className,
      )}
    />
  );
};

const TextareaInput = ({
  inputId,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  validationAttributes,
  className,
}: DefaultInputProps) => (
  <textarea
    id={inputId}
    name={name}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onBlur={onBlur}
    placeholder={placeholder}
    rows={4}
    {...validationAttributes}
    className={tw(
      "focus:border-indigo focus:ring-indigo border-1 w-full resize-y rounded-lg border-neutral-400 p-2.5 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1",
      error && "border-red-500 focus:border-red-500 focus:ring-red-500",
      className,
    )}
  />
);

export const DateOfBirthInput = ({
  inputId,
  labelId,
  required,
  name,
  value,
  onChange,
  onBlur,
  error,
  validationAttributes,
  className,
}: DateOfBirthInputProps) => {
  const [parts, setParts] = useState({ mm: "", dd: "", yyyy: "" });

  const refs = {
    mm: useRef<HTMLInputElement>(null),
    dd: useRef<HTMLInputElement>(null),
    yyyy: useRef<HTMLInputElement>(null),
  };

  useEffect(() => {
    if (value && value.length === 10) {
      const [mm, dd, yyyy] = value.split("/");
      setParts({ mm, dd, yyyy });
    }
  }, [value]);

  const updateSegment = (segment: DateSegments, digits: string) => {
    const updated = { ...parts, [segment]: digits };

    setParts(updated);
    onChange?.(combineDate(updated.mm, updated.dd, updated.yyyy));
  };

  const handleChange = (segment: DateSegments, raw: string) => {
    const digits = removeNonDigits(raw);
    updateSegment(segment, digits);

    const isDouble = digits.length === 2;
    const isSingle = digits.length === 1;

    if (segment === DATE_SEGMENTS.MONTH) {
      if (isDouble || (isSingle && +digits > 1)) refs.dd.current?.focus();
    } else if (segment === DATE_SEGMENTS.DAY) {
      if (isDouble || (isSingle && +digits > 3)) refs.yyyy.current?.focus();
    }
  };

  const handleSegmentBlur = (segment: DateSegments) => () => {
    const raw = refs[segment].current?.value ?? "";

    updateSegment(segment, applyPad(raw, segment));
  };

  // The three segments are one field to the patient, so the form is only told the
  // field was left once focus lands outside all of them, not when it moves from
  // month to day.
  const handleGroupBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;

    onBlur?.();
  };

  const fields = [
    {
      segment: DATE_SEGMENTS.MONTH,
      accessibleName: "Month",
      maxLength: 2,
      width: "w-3/10",
    },
    {
      segment: DATE_SEGMENTS.DAY,
      accessibleName: "Day",
      maxLength: 2,
      width: "w-3/10",
    },
    {
      segment: DATE_SEGMENTS.YEAR,
      accessibleName: "Year",
      maxLength: 4,
      width: "w-4/10",
    },
  ] as const;

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="flex gap-1"
      onBlur={handleGroupBlur}
    >
      {fields.map((f) => (
        <input
          key={f.segment}
          ref={refs[f.segment]}
          id={`${inputId}-${f.segment}`}
          name={`${name}-${f.segment}`}
          value={parts[f.segment]}
          placeholder={f.segment.toUpperCase()}
          aria-label={f.accessibleName}
          aria-required={required}
          maxLength={f.maxLength}
          onChange={(e) => handleChange(f.segment, e.target.value)}
          onBlur={handleSegmentBlur(f.segment)}
          {...validationAttributes}
          className={tw(
            `focus:border-indigo focus:ring-indigo ${f.width} border-1 rounded-lg border-neutral-400 p-2.5 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1`,
            error && "border-red-500 focus:border-red-500 focus:ring-red-500",
            className,
          )}
        />
      ))}
    </div>
  );
};

const DefaultInput = ({
  inputId,
  name,
  type,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  validationAttributes,
  className,
}: DefaultInputProps) => (
  <input
    id={inputId}
    name={name}
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onBlur={onBlur}
    placeholder={placeholder}
    {...validationAttributes}
    className={tw(
      "focus:border-indigo focus:ring-indigo border-1 w-full rounded-lg border-neutral-400 p-2.5 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1",
      error && "border-red-500 focus:border-red-500 focus:ring-red-500",
      className,
    )}
  />
);

export const FormInput = ({
  label,
  name,
  type,
  value,
  onChange,
  onBlur,
  placeholder,
  required = false,
  error,
  options = [],
  className,
  showRequiredIndicator = true,
  disabled,
  hideLabel = false,
}: FormInputProps) => {
  const inputId = `form-input-${name}`;
  const labelId = `${inputId}-label`;
  const errorId = `${inputId}-error`;
  const isSegmentedGroup = type === "dob";
  const validationAttributes: ValidationAttributes = {
    "aria-required": required,
    "aria-invalid": Boolean(error),
    "aria-describedby": error ? errorId : undefined,
  };

  const inputMap: Record<string, React.FC<any>> = {
    select: SelectInput,
    tel: TelInput,
    date: DateInput,
    textarea: TextareaInput,
    dob: DateOfBirthInput,
    default: DefaultInput,
  };

  const InputComponent = useMemo(() => {
    return inputMap[type] || inputMap.default;
  }, [type]);

  const requiredIndicator = required && showRequiredIndicator && (
    <span aria-hidden="true" className="ml-1 text-red-500">
      *
    </span>
  );

  return (
    <div className="space-y-2">
      {isSegmentedGroup ? (
        <span className={labelClassName}>
          <span id={labelId}>{label}</span>
          {requiredIndicator}
        </span>
      ) : (
        <label
          htmlFor={inputId}
          className={tw(labelClassName, hideLabel && "sr-only")}
        >
          {label}
          {requiredIndicator}
        </label>
      )}
      <InputComponent
        inputId={inputId}
        labelId={labelId}
        required={required}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        options={options}
        error={error}
        validationAttributes={validationAttributes}
        className={className}
        disabled={disabled}
      />{" "}
      {error && (
        <p
          id={errorId}
          className="flex items-center gap-1 text-sm text-red-600"
        >
          <icons.outline.ExclamationCircleIcon className="size-4 min-w-4" />
          {error}
        </p>
      )}
    </div>
  );
};
